import { useMutation, useQuery } from "@tanstack/react-query";
import { CaptionApi } from "../api/caption-api";
import {
  CreateCustomCaptionDto,
  CreateCustomCaptionResponseDto,
  UpdateCustomCaptionDto,
} from "@repo/api/caption";
import { queryClient } from "./query-client";
import Toast from "../components/Toast";

export function useCustomCaptionsQuery(teamExternalId: string) {
  return useQuery({
    queryKey: captionQueryKeys.getCustomCaptions(teamExternalId),
    queryFn: () => CaptionApi.getCustomCaptions(teamExternalId),
    staleTime: Infinity,
    meta: {
      errorMessage: "Failed to fetch custom captions",
    },
  });
}

export function useCreateCustomCaptionMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: (data: CreateCustomCaptionResponseDto) => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: captionQueryKeys.createCustomCaption(teamExternalId),
    mutationFn: (data: CreateCustomCaptionDto) =>
      CaptionApi.createCustomCaption(teamExternalId, data),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: captionQueryKeys.getCustomCaptions(teamExternalId),
      });
      onSuccess?.(data);
    },
    onError: (error) => {
      Toast.error("Failed to save preset");
      onError?.(error);
    },
  });
}

export function useUpdateCustomCaptionMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: captionQueryKeys.updateCustomCaption(teamExternalId),
    mutationFn: ({
      captionExternalId,
      data,
    }: {
      captionExternalId: string;
      data: UpdateCustomCaptionDto;
    }) =>
      CaptionApi.updateCustomCaption(teamExternalId, captionExternalId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: captionQueryKeys.getCustomCaptions(teamExternalId),
      });
      onSuccess?.();
    },
    onError: (error) => {
      Toast.error("Failed to update preset");
      onError?.(error);
    },
  });
}

export function useDeleteCustomCaptionMutation({
  teamExternalId,
  onSuccess,
  onError,
}: {
  teamExternalId: string;
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  return useMutation({
    mutationKey: captionQueryKeys.deleteCustomCaption(teamExternalId),
    mutationFn: (captionExternalId: string) =>
      CaptionApi.deleteCustomCaption(teamExternalId, captionExternalId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: captionQueryKeys.getCustomCaptions(teamExternalId),
      });
      onSuccess?.();
    },
    onError: (error) => {
      Toast.error("Failed to delete preset");
      onError?.(error);
    },
  });
}

export const captionQueryKeys = {
  all: () => ["custom-caption"],
  getCustomCaptions: (teamExternalId: string) => [
    "custom-caption",
    "list",
    { teamExternalId },
  ],
  createCustomCaption: (teamExternalId: string) => [
    "custom-caption",
    "create",
    { teamExternalId },
  ],
  updateCustomCaption: (teamExternalId: string) => [
    "custom-caption",
    "update",
    { teamExternalId },
  ],
  deleteCustomCaption: (teamExternalId: string) => [
    "custom-caption",
    "delete",
    { teamExternalId },
  ],
};
